import { useEffect, useMemo, useState } from 'react';
import type { LineDecision, MeResponse, PendingSupplier, PurchaseOptions, PurchaseSourceAiEstimate, PurchaseSourceView, VatMode } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatDate, formatMoney, hasPermission, UNIT_LABEL } from '../format';
import { VAT_LABEL } from './Purchases';

// Sənəddən mədaxil (KT-67): təchizatçının sənədi (Excel/CSV/e-qaimə XML — dərhal; PDF/şəkil — AI ilə, düymə ilə) →
// solda sənəd və təchizatçı, sağda sətirlər. Heç nə avtomatik yazılmır: hər sətir ayrıca təsdiqlənir,
// qaralama alış yalnız təsdiqlənmiş sətirlərdən yaranır, keçirmə adi alış formasındadır.

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.xlsx,.csv,.txt,.xml';
const VIA_LABEL = { barcode: 'barkodla', alias: 'əvvəlki sənəddən', name: 'adla' } as const;

type SupplierChoice = { kind: 'existing'; id: string } | { kind: 'create'; name: string; voen: string } | null;

const num = (s: string | null) => (s === null ? null : Number(s));
// Yalnız göstərmək üçün (hesab serverdədir)
const money = (s: string | null) => (s === null ? '—' : formatMoney(Number(s)));
/** Onluq sətir ekranda vergüllə: «12.5» → «12,5». */
const dec = (s: string | null) => (s === null ? '?' : s.replace('.', ','));
/** Ad artıq dırnaqdadırsa («…» MMC) təkrar dırnaq qoyulmur. */
const quoted = (s: string | null) => (s === null ? '' : /[«"“]/.test(s) ? s : `«${s}»`);
/** AI xərci: qəpikdən az — «< 0,01 ₼». */
const aiMoney = (s: string) => (Number(s) > 0 && Number(s) < 0.01 ? '< 0,01 ₼' : formatMoney(Number(s)));

export function PurchaseSource({ me, onBack, onDraft }: { me: MeResponse; onBack: () => void; onDraft: (docId: string) => void }) {
  const [view, setView] = useState<PurchaseSourceView | null>(null);
  const [options, setOptions] = useState<PurchaseOptions | null>(null);
  const [estimate, setEstimate] = useState<PurchaseSourceAiEstimate | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [supplier, setSupplier] = useState<SupplierChoice>(null);
  const [lines, setLines] = useState<Record<string, LineDecision>>({});
  const [warehouseId, setWarehouseId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canAi = hasPermission(me, 'ai.ask');

  useEffect(() => {
    api.purchaseOptions().then((o) => {
      setOptions(o);
      if (o.warehouses.length > 0) setWarehouseId(o.warehouses[0].id);
    }).catch((err: unknown) => setError(err instanceof ApiError ? `Təchizatçı və anbar siyahısı yüklənmədi: ${err.message}` : 'Təchizatçı və anbar siyahısı yüklənmədi — səhifəni yeniləyin'));
  }, []);

  // Sənəd gələndə: serverin uyğunluğu ilkin seçim olur (təsdiq — istifadəçinin)
  function load(v: PurchaseSourceView) {
    setView(v);
    const s = v.supplierDecision;
    if (s?.counterpartyId) setSupplier({ kind: 'existing', id: s.counterpartyId });
    else if (s?.create) setSupplier({ kind: 'create', name: s.create.name, voen: s.create.voen ?? '' });
    else if (v.supplier.kind === 'voen' && v.supplier.counterpartyId) setSupplier({ kind: 'existing', id: v.supplier.counterpartyId });
    else setSupplier(null);
    setLines(Object.fromEntries(v.lines.map((l) => [String(l.n), l.decision ?? { productId: l.match.productId, confirmed: false }])));
    setEstimate(null);
    setEstimateError(null);
    if (v.status === 'ai_pending' && canAi) {
      api.sourceAiEstimate(v.id).then(setEstimate).catch((err: unknown) => setEstimateError(err instanceof ApiError ? err.message : 'Xərc hesablanmadı'));
    }
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Xəta baş verdi');
    } finally {
      setBusy(false);
    }
  }

  function pick(file: File | undefined) {
    if (!file) return;
    // Hər fayl seçimi — yeni id; şəbəkə kəsilib təkrar göndəriləndə eyni sənəd
    const id = crypto.randomUUID();
    void run(async () => load(await api.sourceUpload(file, id)));
  }

  function patch(n: number, d: Partial<LineDecision>) {
    setLines((cur) => ({ ...cur, [String(n)]: { ...(cur[String(n)] ?? { confirmed: false }), ...d } }));
  }

  const ready = useMemo(() => (view ? view.lines.filter((l) => lines[String(l.n)]?.confirmed).length : 0), [view, lines]);

  function confirmMatched() {
    if (!view) return;
    setLines((cur) => {
      const next = { ...cur };
      for (const l of view.lines) {
        const d = next[String(l.n)];
        if (d && (d.productId || d.newProduct) && l.qty !== null && l.unitPrice !== null) next[String(l.n)] = { ...d, confirmed: true };
      }
      return next;
    });
  }

  function draft() {
    if (!view) return;
    void run(async () => {
      const body = {
        supplier: supplier === null ? null : supplier.kind === 'existing' ? { counterpartyId: supplier.id } : { create: { name: supplier.name.trim(), voen: supplier.voen.trim() || null } },
        lines,
      };
      await api.sourceDecisions(view.id, body);
      const res = await api.sourceDraft(view.id, warehouseId);
      onDraft(res.docId);
    });
  }

  return (
    <div className="shell">
      <header className="topbar">
        <button type="button" className="link-btn back-home" onClick={onBack}>
          ← Alış
        </button>
        <span className="brand">Sənəddən alış</span>
      </header>
      <main className="page">
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        {!view && (
          <section className="card src-drop">
            <h2>Təchizatçının qaiməsindən doldur</h2>
            <p className="muted">
              Excel, CSV və e-qaimə XML dərhal oxunur. PDF və şəkil AI ilə oxunur — xərci əvvəlcədən göstərilir. Sən hər sətri yoxlayıb təsdiqləyirsən.
            </p>
            <label className="btn-primary file-btn">
              {busy ? 'Yüklənir…' : 'Fayl seç'}
              <input type="file" accept={ACCEPT} disabled={busy} onChange={(e) => pick(e.target.files?.[0])} hidden />
            </label>
          </section>
        )}
        {view && view.status === 'ai_pending' && (
          <section className="card">
            <h2>{view.fileName}</h2>
            {canAi ? (
              <>
                <p>
                  Bu sənəd AI ilə oxunacaq{estimate?.pages ? ` (${estimate.pages} səhifə)` : ''}. Təxmini xərc:{' '}
                  <b className="mono">{estimate ? aiMoney(estimate.estimatedAzn) : estimateError ? '—' : '…'}</b>
                  {estimate?.remainingAzn && <span className="muted"> · bu ay qalan limit {formatMoney(Number(estimate.remainingAzn))}</span>}
                </p>
                {estimateError && <p className="bad small">{estimateError}</p>}
              </>
            ) : (
              <p className="muted">AI icazəniz yoxdur — sənədi adi alış formasında əl ilə daxil edin.</p>
            )}
            <div className="src-actions">
              {canAi && (
                <button type="button" className="btn-primary" disabled={busy} onClick={() => void run(async () => load(await api.sourceAiRead(view.id)))}>
                  {busy ? 'Oxunur…' : 'AI ilə oxu'}
                </button>
              )}
              <button type="button" className="link-btn" onClick={() => setView(null)}>
                Başqa fayl seç
              </button>
            </div>
          </section>
        )}
        {view && view.status !== 'ai_pending' && (
          <div className="src-grid">
            <section className="card src-side">
              <h2>Sənəd</h2>
              <p className="muted small">
                {view.fileName}
                {view.ai && ` · AI: ${aiMoney(view.ai.costAzn)}`}
              </p>
              <dl className="src-head">
                <dt>VÖEN</dt>
                <dd className="mono">{view.header.supplierVoen ?? '—'}</dd>
                <dt>Nömrə</dt>
                <dd className="mono">{view.header.docNo ?? '—'}</dd>
                <dt>Tarix</dt>
                <dd>{view.header.docDate ? formatDate(view.header.docDate) : '—'}</dd>
                <dt>Sətirlərin cəmi</dt>
                <dd className="mono">{money(view.totals.lines)}</dd>
                <dt>Sənədin cəmi</dt>
                <dd className={`mono${view.totals.diff && num(view.totals.diff) !== 0 ? ' bad' : ''}`}>{money(view.totals.doc)}</dd>
              </dl>
              <SupplierBlock view={view} options={options} value={supplier} onChange={setSupplier} />
              {view.warnings.length > 0 && (
                <ul className="src-warn">
                  {view.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}
              {view.status === 'parsed' ? (
                <div className="form-grid">
                  <label>
                    Anbar
                    <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                      {options?.warehouses.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name} · {w.outletName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button type="button" className="btn-primary" disabled={busy || !options || ready === 0 || !warehouseId || (supplier?.kind === 'create' && !supplier.name.trim())} onClick={draft}>
                    Qaralama yarat ({ready} sətir)
                  </button>
                  <p className="muted small">Yalnız təsdiqlənmiş sətirlər düşür. Keçirmə — alış formasında.</p>
                </div>
              ) : (
                view.docId && (
                  <button type="button" className="btn-primary" onClick={() => onDraft(view.docId!)}>
                    Qaralamanı aç
                  </button>
                )
              )}
            </section>
            <section className="card src-lines">
              <div className="src-lines-head">
                <h2>Mallar · {view.lines.length}</h2>
                {view.status === 'parsed' && (
                  <button type="button" className="btn-small" onClick={confirmMatched}>
                    Tapılanların hamısını təsdiqlə
                  </button>
                )}
              </div>
              {view.lines.length === 0 && <p className="muted">Sətir oxunmadı.</p>}
              <ol className="src-list">
                {view.lines.map((l) => (
                  <LineRow key={l.n} line={l} decision={lines[String(l.n)]} locked={view.status !== 'parsed'} onPatch={(d) => patch(l.n, d)} />
                ))}
              </ol>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

function SupplierBlock({ view, options, value, onChange }: { view: PurchaseSourceView; options: PurchaseOptions | null; value: SupplierChoice; onChange: (v: SupplierChoice) => void }) {
  const m = view.supplier;
  const suggested = m.officialName ?? view.header.supplierName ?? '';
  const locked = view.status !== 'parsed';
  return (
    <fieldset className="src-supplier" disabled={locked}>
      <legend>Təchizatçı</legend>
      {m.kind === 'voen' && (
        <p className="ok small">
          VÖEN ilə tanındı: <b>{m.counterpartyName}</b>
          {m.pending && ' (təsdiq gözləyir)'}
        </p>
      )}
      {m.kind === 'name' && <p className="small">Sənəddə: {quoted(view.header.supplierName)}. Bu, aşağıdakılardan biridirmi?</p>}
      {m.kind === 'catalog' && <p className="small">Kataloqda rəsmi ad: {quoted(m.officialName)} — sizdə yoxdur.</p>}
      {m.kind === 'new' && <p className="small">Təchizatçı tanınmadı{view.header.supplierName ? `: ${quoted(view.header.supplierName)}` : ''}.</p>}
      {m.candidates.map((c) => (
        <label key={c.id} className="radio-row">
          <input type="radio" checked={value?.kind === 'existing' && value.id === c.id} onChange={() => onChange({ kind: 'existing', id: c.id })} />
          {c.name}
          {c.voen && <span className="muted mono small"> {c.voen}</span>}
        </label>
      ))}
      {m.kind !== 'voen' && (
        <>
          <label className="radio-row">
            <input type="radio" checked={value?.kind === 'create'} onChange={() => onChange({ kind: 'create', name: suggested, voen: view.header.supplierVoen ?? '' })} />
            Yeni təchizatçı kimi əlavə et
          </label>
          {value?.kind === 'create' && (
            <div className="form-grid">
              <label>
                Ad
                <input value={value.name} maxLength={200} onChange={(e) => onChange({ ...value, name: e.target.value })} />
              </label>
              <label>
                VÖEN
                <input value={value.voen} inputMode="numeric" maxLength={10} onChange={(e) => onChange({ ...value, voen: e.target.value.replace(/\D/g, '') })} />
              </label>
              <p className="muted small">Sahib və ya mühasib təsdiqləyənə qədər alış qaralama qalır.</p>
            </div>
          )}
          <label className="radio-row">
            <input type="radio" checked={value?.kind === 'existing' && !m.candidates.some((c) => c.id === value.id)} onChange={() => onChange(null)} hidden />
            <select
              aria-label="Mövcud təchizatçı"
              value={value?.kind === 'existing' && !m.candidates.some((c) => c.id === value.id) ? value.id : ''}
              onChange={(e) => onChange(e.target.value ? { kind: 'existing', id: e.target.value } : null)}
            >
              <option value="">Başqa mövcud təchizatçı…</option>
              {options?.suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
    </fieldset>
  );
}

function LineRow({ line, decision, locked, onPatch }: {
  line: PurchaseSourceView['lines'][number];
  decision: LineDecision | undefined;
  locked: boolean;
  onPatch: (d: Partial<LineDecision>) => void;
}) {
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<{ id: string; name: string }[]>([]);
  const [searchFailed, setSearchFailed] = useState(false);
  const d = decision ?? { confirmed: false };
  const chosen = d.productId ? (d.productId === line.match.productId ? line.match.productName : line.match.candidates.find((c) => c.id === d.productId)?.name ?? found.find((c) => c.id === d.productId)?.name ?? 'Seçilmiş mal') : null;
  const unreadable = line.qty === null || line.unitPrice === null;

  useEffect(() => {
    if (search.trim().length < 2) {
      setFound([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .products({ q: search.trim(), limit: 8 })
        .then((r) => {
          setSearchFailed(false);
          setFound(r.items.map((p) => ({ id: p.id, name: p.name })));
        })
        .catch(() => {
          setSearchFailed(true);
          setFound([]);
        });
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <li className={`src-line${d.confirmed ? ' ok' : ''}${line.confidence === 'low' || line.issues.length > 0 ? ' warn' : ''}`}>
      <div className="src-line-top">
        <span className="src-name">
          <span className="muted mono small">{line.n}.</span> {line.name}
          {line.barcode && <span className="muted mono small"> {line.barcode}</span>}
        </span>
        <span className="mono src-nums">
          {dec(line.qty)} {line.unit ?? ''} × {dec(line.unitPrice)}
          {line.priceBasis === 'net' ? ' (ƏDV-siz)' : line.priceBasis === 'gross' ? ' (ƏDV daxil)' : ''} = {money(line.total)}
        </span>
      </div>
      {(line.issues.length > 0 || line.confidence === 'low') && (
        <p className="bad small">{[...(line.confidence === 'low' ? ['AI aydın oxumadı — sənədlə tutuşdurun'] : []), ...line.issues].join(' · ')}</p>
      )}
      <div className="src-line-match">
        {d.newProduct ? (
          <span className="src-new">
            Yeni mal:
            <input aria-label="Yeni malın adı" value={d.newProduct.name} disabled={locked} maxLength={200} onChange={(e) => onPatch({ newProduct: { ...d.newProduct!, name: e.target.value } })} />
            <select aria-label="Vahid" value={d.newProduct.unitCode} disabled={locked} onChange={(e) => onPatch({ newProduct: { ...d.newProduct!, unitCode: e.target.value } })}>
              {Object.entries(UNIT_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <select aria-label="ƏDV növü" value={d.newProduct.vatMode} disabled={locked} onChange={(e) => onPatch({ newProduct: { ...d.newProduct!, vatMode: e.target.value as VatMode } })}>
              {(Object.keys(VAT_LABEL) as VatMode[]).map((k) => (
                <option key={k} value={k}>
                  {VAT_LABEL[k]}
                </option>
              ))}
            </select>
          </span>
        ) : chosen ? (
          <span>
            → <b>{chosen}</b>
            {d.productId === line.match.productId && line.match.via && <span className="muted small"> ({VIA_LABEL[line.match.via]})</span>}
          </span>
        ) : (
          <span className="muted">Mal seçilməyib</span>
        )}
        {!locked && (
          <span className="src-line-actions">
            {line.match.candidates.length > 0 && !d.productId && !d.newProduct && (
              <select aria-label="Oxşar mallar" value="" onChange={(e) => e.target.value && onPatch({ productId: e.target.value, newProduct: null })}>
                <option value="">Oxşar mallar…</option>
                {line.match.candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            <input className="src-search" aria-label="Mal axtar" placeholder="Mal axtar…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {!d.newProduct && (
              <button type="button" className="link-btn" onClick={() => onPatch({ productId: null, newProduct: { name: line.name, unitCode: 'pcs', vatMode: line.vatRate === '0' ? 'vat0' : 'vat18' }, confirmed: false })}>
                Yeni mal
              </button>
            )}
            {(d.productId || d.newProduct) && (
              <button type="button" className="link-btn" onClick={() => onPatch({ productId: null, newProduct: null, confirmed: false })}>
                Sıfırla
              </button>
            )}
            <label className="src-confirm">
              <input type="checkbox" checked={d.confirmed} disabled={unreadable || (!d.productId && !d.newProduct)} onChange={(e) => onPatch({ confirmed: e.target.checked })} />
              Təsdiq
            </label>
          </span>
        )}
      </div>
      {searchFailed && <p className="bad small">Axtarış alınmadı — yenidən yoxlayın</p>}
      {found.length > 0 && (
        <ul className="src-found">
          {found.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  onPatch({ productId: p.id, newProduct: null });
                  setSearch('');
                  setFound([p]);
                }}
              >
                {p.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** Təsdiq gözləyən təchizatçılar (sahib/mühasib): təsdiqlə / mövcud ilə birləşdir / rədd et. */
export function PendingSuppliers({ onChanged }: { onChanged?: () => void }) {
  const [items, setItems] = useState<PendingSupplier[] | null>(null);
  const [into, setInto] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function reload() {
    api.pendingSuppliers().then(setItems).catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Siyahı yüklənmədi'));
  }
  useEffect(reload, []);

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      reload();
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Xəta baş verdi');
    } finally {
      setBusy(false);
    }
  }

  if (!items || items.length === 0) return error ? <p className="bad">{error}</p> : null;
  return (
    <section className="card pending-suppliers" aria-label="Təsdiq gözləyən təchizatçılar">
      <h2>Təsdiq gözləyən təchizatçılar · {items.length}</h2>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      <ul className="src-list">
        {items.map((s) => (
          <li key={s.id} className="src-line">
            <div className="src-line-top">
              <span className="src-name">
                <b>{s.name}</b>
                {s.voen && <span className="muted mono small"> VÖEN {s.voen}</span>}
                {s.officialName && s.officialName !== s.name && <span className="muted small"> · rəsmi ad: {s.officialName}</span>}
              </span>
              <span className="muted small">
                {s.drafts.length} qaralama alış{s.drafts.length > 0 && ` · ${s.drafts.map((d) => d.displayNo).join(', ')}`}
              </span>
            </div>
            <div className="src-line-match">
              <button type="button" className="btn-small" disabled={busy} onClick={() => void act(() => api.supplierApprove(s.id))}>
                Təsdiqlə
              </button>
              {s.similar.length > 0 && (
                <>
                  <select aria-label="Birləşdiriləcək təchizatçı" value={into[s.id] ?? ''} onChange={(e) => setInto((c) => ({ ...c, [s.id]: e.target.value }))}>
                    <option value="">Mövcud ilə birləşdir…</option>
                    {s.similar.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                        {x.voen ? ` (${x.voen})` : ''}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="btn-small" disabled={busy || !into[s.id]} onClick={() => void act(() => api.supplierMerge(s.id, into[s.id]))}>
                    Birləşdir
                  </button>
                </>
              )}
              <button type="button" className="link-btn bad" disabled={busy} onClick={() => void act(() => api.supplierReject(s.id))}>
                Rədd et
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
